import { action, computed, makeObservable, observable } from 'mobx';
import { Service } from 'typedi';

import type { IApiError } from '../services/apiError';
import {
  AnnouncementStatus,
  AnnouncementType,
  type IAnnouncementEntity,
  type IAnnouncementLocalization,
  type IDraftFields,
  type IRedirect,
} from '../types/announcement';
import { fromLocalInput, newLocalizations, toLocalInput, toRequestLocalizations } from '../utils/announcement';
import { type IAudienceDraft, toAudienceDraft, toRequestAudience } from '../utils/audience';
import { type TValidationReason, validateAnnouncement } from '../utils/validation';

export interface IAnnouncementDraft {
  type: AnnouncementType;
  localizations: IAnnouncementLocalization[];
  important: boolean;
  pinned: boolean;
  audience: IAudienceDraft;
  /** Publish now or at `publishAt`. */
  when: 'now' | 'at';
  /** datetime-local values in the manager's local time; '' = unset. */
  publishAt: string;
  endAt: string;
}

export type TFormLoad =
  | { status: 'loading' }
  | { status: 'ready'; announcement?: IAnnouncementEntity; copyOf?: IAnnouncementEntity }
  | { status: 'versionCardCopy' }
  | { status: 'failed'; error: IApiError };

export type TFormConfirm = 'publish' | 'withdraw' | null;

const newDraft = (): IAnnouncementDraft => ({
  type: AnnouncementType.ANNOUNCEMENT_TYPE_NEWS,
  localizations: newLocalizations(),
  important: false,
  pinned: false,
  audience: toAudienceDraft(undefined),
  when: 'now',
  publishAt: '',
  endAt: '',
});

const toDraft = (a: IAnnouncementEntity): IAnnouncementDraft => ({
  type: a.type,
  localizations: a.localizations,
  important: a.important,
  pinned: a.pinned,
  audience: toAudienceDraft(a.audience),
  when: a.publishAt ? 'at' : 'now',
  publishAt: toLocalInput(a.publishAt),
  endAt: toLocalInput(a.endAt),
});

/** A new draft from an existing announcement: everything but the dates, which belong to the old one. Update-type
 * announcements here are update stories (cards have their own form), so a copy keeps its type. */
const copyDraft = (a: IAnnouncementEntity): IAnnouncementDraft => ({
  ...toDraft(a),
  when: 'now',
  publishAt: '',
  endAt: '',
});

@Service()
export class AnnouncementFormStore {
  private _load: TFormLoad = { status: 'ready' };
  private _draft: IAnnouncementDraft = newDraft();
  private _busy = false;
  private _serverError: IApiError | null = null;
  private _confirming: TFormConfirm = null;
  private _notifyAgain = false;
  private _redirect: IRedirect | null = null;
  /** Id whose data is in the store; saving a new announcement sets it before the URL changes. */
  private _loadedId: string | null = null;
  /** The id the latest Get is for: a late answer for an earlier id is dropped. */
  private _requestedId: string | null = null;

  constructor() {
    makeObservable<
      AnnouncementFormStore,
      | '_load'
      | '_draft'
      | '_busy'
      | '_serverError'
      | '_confirming'
      | '_notifyAgain'
      | '_redirect'
      | '_loadedId'
      | '_requestedId'
    >(this, {
      _load: observable.ref,
      _draft: observable.ref,
      _busy: observable,
      _serverError: observable.ref,
      _confirming: observable,
      _notifyAgain: observable,
      _redirect: observable.ref,
      _loadedId: observable,
      _requestedId: observable,
      load: computed,
      draft: computed,
      busy: computed,
      serverError: computed,
      confirming: computed,
      notifyAgain: computed,
      redirect: computed,
      loadedId: computed,
      requestedId: computed,
      announcement: computed,
      published: computed,
      scheduled: computed,
      readOnly: computed,
      reset: action,
      setRequestedId: action,
      setLoad: action,
      show: action,
      startCopy: action,
      setField: action,
      startAction: action,
      finishAction: action,
      setServerError: action,
      setConfirming: action,
      setNotifyAgain: action,
      setRedirect: action,
      clearRedirect: action,
    });
  }

  get load(): TFormLoad {
    return this._load;
  }
  get draft(): IAnnouncementDraft {
    return this._draft;
  }
  get busy(): boolean {
    return this._busy;
  }
  get serverError(): IApiError | null {
    return this._serverError;
  }
  get confirming(): TFormConfirm {
    return this._confirming;
  }
  get notifyAgain(): boolean {
    return this._notifyAgain;
  }
  get redirect(): IRedirect | null {
    return this._redirect;
  }
  get loadedId(): string | null {
    return this._loadedId;
  }
  get requestedId(): string | null {
    return this._requestedId;
  }

  /** The stored announcement on screen; none for a new one. */
  get announcement(): IAnnouncementEntity | undefined {
    return this._load.status === 'ready' ? this._load.announcement : undefined;
  }
  get published(): boolean {
    return this.announcement?.status === AnnouncementStatus.ANNOUNCEMENT_STATUS_PUBLISHED;
  }
  get scheduled(): boolean {
    return this.announcement?.status === AnnouncementStatus.ANNOUNCEMENT_STATUS_SCHEDULED;
  }
  get readOnly(): boolean {
    const status = this.announcement?.status;
    return (
      status === AnnouncementStatus.ANNOUNCEMENT_STATUS_WITHDRAWN ||
      status === AnnouncementStatus.ANNOUNCEMENT_STATUS_EXPIRED
    );
  }

  // Plain getters (not `computed`): `reason` reads the clock, so it is evaluated on every render as before.
  get publishAt(): Date | undefined {
    return this._draft.when === 'at' && !this.published ? fromLocalInput(this._draft.publishAt) : undefined;
  }
  get endAt(): Date | undefined {
    return fromLocalInput(this._draft.endAt);
  }
  get audience() {
    return toRequestAudience(this._draft.audience);
  }
  get reason(): TValidationReason | null {
    return validateAnnouncement({
      type: this._draft.type,
      localizations: this._draft.localizations,
      publishAt: this.publishAt,
      endAt: this.endAt,
      audience: this.audience,
    });
  }
  /** CreateDraft / UpdateDraft payload of the form. */
  get requestFields(): IDraftFields {
    return {
      type: this._draft.type,
      localizations: toRequestLocalizations(this._draft.localizations),
      important: this._draft.important,
      pinned: this._draft.pinned,
      audience: this.audience,
      publishAt: this.publishAt,
      endAt: this.endAt,
    };
  }

  /** A new visit of the form screen. */
  reset = (): void => {
    this._load = { status: 'ready' };
    this._draft = newDraft();
    this._busy = false;
    this._serverError = null;
    this._confirming = null;
    this._notifyAgain = false;
    this._redirect = null;
    this._loadedId = null;
    this._requestedId = null;
  };

  setRequestedId = (id: string): void => {
    this._requestedId = id;
  };
  setLoad = (load: TFormLoad): void => {
    this._load = load;
  };
  /** Shows a stored announcement in the form. */
  show = (a: IAnnouncementEntity): void => {
    this._loadedId = a.id;
    this._draft = toDraft(a);
    this._load = { status: 'ready', announcement: a };
  };
  /** «Создать на основе»: the source becomes an unsaved new draft. */
  startCopy = (source: IAnnouncementEntity): void => {
    this._draft = copyDraft(source);
    this._load = { status: 'ready', copyOf: source };
  };
  setField = <K extends keyof IAnnouncementDraft>(key: K, value: IAnnouncementDraft[K]): void => {
    this._draft = { ...this._draft, [key]: value };
  };
  startAction = (): void => {
    this._busy = true;
    this._serverError = null;
  };
  finishAction = (): void => {
    this._busy = false;
  };
  setServerError = (error: IApiError): void => {
    this._serverError = error;
  };
  setConfirming = (confirming: TFormConfirm): void => {
    this._confirming = confirming;
  };
  setNotifyAgain = (notifyAgain: boolean): void => {
    this._notifyAgain = notifyAgain;
  };
  setRedirect = (redirect: IRedirect): void => {
    this._redirect = redirect;
  };
  clearRedirect = (): void => {
    this._redirect = null;
  };
}
